import { MutableRefObject, PropsWithChildren, JSX, useEffect, CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { useClickOutside, useMobileView } from '@betgames/bg-tools';
import {
    CONTAINER_WIDTH,
    ROOT_CLASS,
    useHeightChange,
    useModalsManager,
    ModalName,
} from '@bg-shared';
import classes from './Modal.module.scss';

export type IFade = (ref: MutableRefObject<HTMLDivElement>, complete?: () => void) => void;

interface IProps {
    modalName: ModalName;
    className?: string;
    animation?: {
        fadeIn?: IFade;
        fadeOut?: IFade;
        fadeInClassName?: string;
        fadeOutClassName?: string;
        duration?: number;
    };
    parentSelector?: string;
    backdropClassName?: string;
    onClose?(): void;
    showBackdrop?: boolean;
    style?: CSSProperties;
    openerElementRef?: MutableRefObject<HTMLElement>;
}

export const Modal = ({
    parentSelector = `.${ROOT_CLASS}`,
    ...props
}: PropsWithChildren<IProps>): JSX.Element => {
    const isMobileView = useMobileView(CONTAINER_WIDTH);
    const modalManager = useModalsManager();
    const modalItem = modalManager.getModal(props.modalName);
    const container = document.querySelector(parentSelector);
    const ref = useHeightChange(props.modalName);
    const showBackdrop = props.showBackdrop ?? isMobileView;

    const removeModal = (): void => {
        modalManager.remove(props.modalName);
    };

    useClickOutside((target) => {
        if (
            showBackdrop ||
            !props.openerElementRef ||
            ref.current?.contains(target) ||
            props.openerElementRef?.current?.contains(target)
        ) {
            return;
        }
        props.onClose();
    });

    useEffect(() => {
        if (!props.animation) {
            if (modalItem?.hide) {
                removeModal();
            }
            return;
        }

        if (modalItem?.hide) {
            if (props.animation.fadeOut) {
                props.animation.fadeOut(ref, removeModal);
            } else if (props.animation.fadeOutClassName) {
                setTimeout(removeModal, props.animation.duration || 300);
            }
        } else if (props.animation.fadeIn) {
            props.animation.fadeIn(ref);
        }
    }, [modalItem?.hide]);

    if (!container) {
        return null;
    }

    return createPortal(
        <>
            {showBackdrop && !modalItem?.hide && (
                <div
                    role="presentation"
                    className={classNames(classes.backdrop, props.backdropClassName)}
                    onClick={props.onClose}
                />
            )}
            <div
                id={props.modalName}
                className={classNames(classes.container, props.className, {
                    [props.animation?.fadeInClassName]: !modalItem?.hide,
                    [props.animation?.fadeOutClassName]: modalItem?.hide,
                })}
                data-qa={`area-modal-${props.modalName}`}
                ref={ref}
                style={props.style}
            >
                {props.children}
            </div>
        </>,
        container,
    );
};
